// The print sheet visual system (phase 2, surface 10).
//
// refresh_plan.md section 4 row 10 calls the print sheets "a separate visual
// system, separate pass", and this module is that system's single source of
// truth. It is deliberately NOT the screen token system: a print sheet is a
// paper document that leaves the building stapled to freight, gets
// photocopied, faxed, and read on a loading dock in direct sun. Its palette
// is black ink on white paper, full stop — no `var(--lw-*)` reads, because a
// Printd iframe contains none of the app's stylesheets and a screen token
// would resolve to nothing there, and no greys pale enough to die in a
// second-generation photocopy.
//
// Before this pass the four print components carried two divergent hand-made
// systems: loadsPrint/subhaulerPrint shared a 20px float-layout `.dispatch-
// print` block (pasted into both files AND into ui/src/sass/_print.scss),
// while batchPrint/billingPrint shared a different 14px `.invoice` block
// (pasted into both of those). Same class names, different files, drifting
// values — the exact copy-paste failure the design refresh exists to end,
// reproduced in miniature inside the print path.
//
// HOW A SHEET IS BUILT. Each print component's `cssText` is
// `SHEET_BASE_CSS + <its own extras>`, so every sheet stays self-contained:
// the printer contract (`$printer.print(content.$el, [content.cssText])`)
// hands the iframe one stylesheet and that stylesheet must carry everything
// the sheet needs. This module keeps the sheets consistent at the source
// level; the string still travels whole with every component.
//
// The vocabulary:
//   .lw-sheet              the page root
//   .lw-sheet__letterhead  org legal name + document title + document date,
//                          under a heavy rule. Every sheet opens with one, so
//                          every paper this product emits answers "who issued
//                          this and what is it" the same way in the same place.
//   .lw-sheet__label       the small tracked-caps key style ("BILL TO", "ORD #")
//   .lw-sheet__meta*       key/value blocks between the letterhead and a table
//   .lw-sheet__table       the one table style: tracked-caps header row over a
//                          heavy rule, hairline row rules, no zebra (stripes
//                          cost toner and photocopy poorly)
//   .lw-sheet__totals*     right-aligned totals block with a heavy rule above
//                          the final figure
//   .lw-sheet__note        free-text prose (Notes, payment terms)
//   .lw-sheet__rule        a section divider
//
// Sizes are in pt because this is paper: 1pt is 1/72in on every printer,
// while px is a CSS abstraction that print engines map back to pt anyway.
export const SHEET_BASE_CSS = `
  * { box-sizing: border-box; }
  html, body {
    margin: 0;
    padding: 0;
    background: white;
  }
  .lw-sheet {
    font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
    color: black;
    background: white;
    font-size: 11pt;
    line-height: 1.45;
    padding: 24pt 28pt;
  }
  .lw-sheet p { margin: 0; padding: 0; }

  .lw-sheet__letterhead {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 12pt;
    border-bottom: 2.25pt solid black;
    padding-bottom: 6pt;
  }
  .lw-sheet__org {
    font-size: 16pt;
    font-weight: 700;
    letter-spacing: 0.02em;
    margin: 0;
  }
  .lw-sheet__doc { text-align: right; }
  .lw-sheet__doc-title {
    font-size: 11pt;
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    margin: 0;
  }
  .lw-sheet__doc-date {
    font-size: 9.5pt;
    margin: 2pt 0 0;
  }

  .lw-sheet__section { margin: 14pt 0 0; }

  .lw-sheet__label {
    display: block;
    font-size: 8.5pt;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    margin: 0 0 2pt;
  }

  .lw-sheet__meta {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 18pt;
  }
  .lw-sheet__meta-item { margin: 0 0 8pt; }
  .lw-sheet__meta-col { min-width: 0; }
  .lw-sheet__meta-col--end { text-align: right; }
  .lw-sheet__meta-row {
    display: flex;
    gap: 18pt;
  }
  .lw-sheet__meta-row .lw-sheet__meta-item { flex: 1 1 0; }

  .lw-sheet__table {
    width: 100%;
    border-collapse: collapse;
  }
  .lw-sheet__table th {
    font-size: 8.5pt;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    text-align: left;
    padding: 3pt 6pt;
    border-bottom: 1.5pt solid black;
    vertical-align: bottom;
  }
  .lw-sheet__table td {
    font-size: 10.5pt;
    padding: 4pt 6pt;
    border-bottom: 0.5pt solid black;
    vertical-align: top;
  }
  .lw-sheet__table .lw-sheet__num {
    text-align: right;
    font-variant-numeric: tabular-nums;
  }

  .lw-sheet__totals {
    margin: 10pt 0 0;
    display: flex;
    justify-content: flex-end;
  }
  .lw-sheet__totals-body { min-width: 180pt; }
  .lw-sheet__totals-row {
    display: flex;
    justify-content: space-between;
    gap: 18pt;
    padding: 2pt 0;
  }
  .lw-sheet__totals-row--grand {
    border-top: 1.5pt solid black;
    margin-top: 3pt;
    padding-top: 4pt;
    font-weight: 700;
  }

  .lw-sheet__note { white-space: pre-wrap; }

  .lw-sheet__rule {
    border: none;
    border-top: 1pt solid black;
    margin: 12pt 0;
  }

  .lw-sheet__footnote {
    font-size: 9pt;
    margin: 10pt 0 0;
  }

  @media print {
    @page {
      margin: 0.5in;
      size: auto;
    }
    .lw-sheet { padding: 0; }
  }
`;

export default SHEET_BASE_CSS;
